import { mergeNormalized, normalizedFromCheckin } from './dataNormalization'

/** A message in the Genie chat transcript. */
export interface GenieMessage {
    id?: number
    role: "system" | "user" | "assistant" | "function" | "checkin" | "list_checkins" | "narrative" | "bulk_edit_prepared" | "selected_checkin_list"
    content: string
    renderedContent?: string
    pending?: boolean
    functionCall?: any
    /** Ids of the checkins a `checkin` message is about, in the order they landed. */
    checkinIds?: number[]
    /** The journal the batch was written under; null for unjournaled single writes. */
    journalId?: number | null
    isUpdated?: boolean
    messageType?: string
    data?: any
    action?: string
    genieMessageId?: number
    freshlyMutatedInSession?: boolean
}

/** The checkin ids a create/update event's payload is about. */
export function eventCheckinIds(event: any): number[] {
    if (Array.isArray(event?.checkin_ids)) return event.checkin_ids
    return event?.checkin_id != null ? [event.checkin_id] : []
}

function normalizedFromEvent(event: any) {
    return {
        checkins: event?.checkins || [],
        features: event?.features || {},
        projects: event?.projects || {},
        collections: event?.collections || {},
        collection_items: event?.collection_items || {},
    }
}

/**
 * Fold a `checkin_created` / `checkin_updated` event into the transcript.
 *
 * A commit writes N checkins one at a time — each is followed by a slow parse,
 * so the events arrive spread out — but they belong to one user action and are
 * stored as one row. Events that share a journal_id (and the same create/update
 * sense) grow the message the previous one made, so a live batch ends up as the
 * single message a reload delivers in one payload. An event without a journal_id
 * is a standalone write and always starts its own message.
 */
export function appendCheckinEvent(
    messages: GenieMessage[],
    event: any,
    options: { isUpdated?: boolean; freshlyMutatedInSession?: boolean } = {}
): GenieMessage[] {
    const isUpdated = !!options.isUpdated
    const journalId = event?.journal_id ?? null
    const incoming = normalizedFromEvent(event)
    const ids = eventCheckinIds(event)

    const last = messages[messages.length - 1]
    const belongsToLast =
        last !== undefined &&
        last.role === "checkin" &&
        !!last.isUpdated === isUpdated &&
        last.journalId != null &&
        last.journalId === journalId

    if (belongsToLast) {
        const known = last.checkinIds || []
        const merged: GenieMessage = {
            ...last,
            data: mergeNormalized(last.data, incoming),
            checkinIds: [...known, ...ids.filter(id => !known.includes(id))],
        }
        return [...messages.slice(0, -1), merged]
    }

    return [...messages, {
        role: "checkin",
        content: "",
        data: incoming,
        checkinIds: ids,
        journalId,
        genieMessageId: event?.genie_message_id,
        isUpdated,
        freshlyMutatedInSession: !!options.freshlyMutatedInSession,
    }]
}

/**
 * Merge a completed parse back into the message that triggered it, so the card
 * picks up the features and projects the parser found. Keyed by the stored
 * message row, since the same checkin can appear in an earlier message whose
 * snapshot should stay as it was.
 */
export function applyParsedCheckin(
    messages: GenieMessage[],
    genieMessageId: number | undefined,
    checkin: any,
    collectionItems: Record<string, any> = {}
): GenieMessage[] {
    if (genieMessageId === undefined) return messages
    const incoming = normalizedFromCheckin(checkin, collectionItems)
    return messages.map(msg =>
        msg.role === "checkin" && msg.genieMessageId === genieMessageId
            ? { ...msg, data: mergeNormalized(msg.data, incoming) }
            : msg
    )
}

/**
 * Merge a freshly fetched normalized payload into every checkin message that
 * holds one of its checkins — what a card's "refresh" action needs after the
 * user edited the checkin elsewhere.
 */
export function applyRefreshedCheckins(
    messages: GenieMessage[],
    normalized: any
): GenieMessage[] {
    const freshIds: number[] = (normalized?.checkins || []).map((c: any) => c.id)
    if (freshIds.length === 0) return messages
    return messages.map(msg =>
        msg.role === "checkin" && (msg.checkinIds || []).some(id => freshIds.includes(id))
            ? { ...msg, data: mergeNormalized(msg.data, normalized) }
            : msg
    )
}
